import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from './Button'
import { ConfirmDialog } from './ConfirmDialog'
import { useFormStore } from '../../store/formStore'
import { saveApplication } from '../../api/applicant'

/**
 * "Clear form & start over" — wipes every filled field and upload (keeping the
 * user signed in) and returns to the beginning of the form. Sits centered just
 * under the primary "Continue" button on each step; replaces the old "Clear
 * form" button that used to live in the header.
 *
 * Because clearing is destructive and can't be undone, the action is gated
 * behind the shared ConfirmDialog (danger tone) rather than firing on the first
 * click.
 */
interface StartOverButtonProps {
  /**
   * When true, drops the centered wrapper + top margin so the button can sit
   * as one item in a row (e.g. top-right, paired with a Back button on the
   * left). Default keeps the original centered-below-Continue placement.
   */
  inline?: boolean
}

export function StartOverButton({ inline = false }: StartOverButtonProps = {}) {
  const clearForm = useFormStore((s) => s.clearForm)
  const [confirming, setConfirming] = useState(false)

  async function handleConfirm() {
    // Null out every saved top-level key server-side (jsonb merge can't delete
    // keys, and hydration treats nulls as absent), then reset the section.
    const { formData, autoFilledFields, uploadStates } = useFormStore.getState()
    const cleared: Record<string, unknown> = { autoFilledFields: null, uploadStates: null }
    for (const key of Object.keys({ ...formData, autoFilledFields, uploadStates })) {
      if (key !== 'authEmail') cleared[key] = null
    }
    try {
      await saveApplication({ form_data: cleared, current_section: 'welcome' })
    } catch {
      // Local clear still proceeds; the next auto-save will retry the wipe.
    }
    clearForm()
    // Full page load (not SPA navigation) so screens with local form state
    // re-initialise from the freshly cleared store.
    window.location.assign('/apply/welcome')
  }

  return (
    <>
      <div
        style={
          inline
            ? { display: 'flex' }
            : { display: 'flex', justifyContent: 'center', marginTop: 'var(--spacing-4)' }
        }
      >
        <Button
          variant="quiet"
          type="button"
          onClick={() => setConfirming(true)}
          title="Clear all filled fields and start the form fresh"
          style={{ padding: 'var(--spacing-1) var(--spacing-2)', fontSize: 'var(--text-body-sm)' }}
        >
          Start Over
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        tone="danger"
        icon={<AlertTriangle size={28} />}
        title="Start over from scratch?"
        description={
          <>
            This will erase everything you&rsquo;ve entered and take you back to the
            first step. This can&rsquo;t be undone.
          </>
        }
        confirmLabel="Clear everything"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  )
}
